import type { CategorySlug, DashboardRange, JobListParams } from "../types";

// Narrow param shapes shared between a query-key factory and its matching
// `queryOptions`/`infiniteQueryOptions` factory. Living here (rather than in
// each domain file) avoids a qk.ts <-> domain-file import cycle, since every
// domain file imports `qk` from this module.

export type FeedTab = "for-you" | "following" | "trending";

export interface ProductListParams {
  q?: string;
  category?: CategorySlug;
  subcategory?: string;
  subscope?: string;
  gameType?: string;
  /** Trailing window in days a product must have been published within. */
  publishedWithin?: number;
  price?: "free" | "paid" | "sale";
  minRating?: number;
  sort?: "popular" | "trending" | "newest" | "price-asc" | "price-desc" | "rating";
  page?: number;
  pageSize?: number;
}

export type StoreListSort = "featured" | "rating" | "products" | "followers";

export interface StoreListParams {
  /** `studio` narrows to studio-owned storefronts; omitted lists every store. */
  kind?: "studio";
  sort?: StoreListSort;
  page?: number;
  pageSize?: number;
}

export interface StoreProductParams {
  q?: string;
  category?: CategorySlug;
  sort?: "newest" | "price-asc" | "price-desc" | "rating";
}

export interface CommunityListParams {
  q?: string;
  category?: string;
}

export interface OrderListParams {
  status?: "completed" | "refunded" | "pending";
  q?: string;
}

/**
 * Single query-key factory for the whole app (plan §4/§5). Every
 * `queryOptions`/`infiniteQueryOptions` factory in `src/api/queries/*`
 * builds its key here so a mutation can reliably enumerate every cached
 * representation of an entity to patch or invalidate.
 */
export const qk = {
  me: () => ["me"] as const,
  feed: (tab: FeedTab) => ["feed", tab] as const,
  trending: () => ["trending"] as const,

  creators: {
    suggested: () => ["creators", "suggested"] as const,
    detail: (username: string) => ["creators", "detail", username] as const,
    posts: (username: string) => ["creators", "detail", username, "posts"] as const,
    products: (username: string) => ["creators", "detail", username, "products"] as const,
  },

  products: {
    /** Every product query; they carry the viewer's `inWishlist`, so they're dropped when the session changes. */
    all: () => ["products"] as const,
    list:(params: ProductListParams) => ["products", "list", params] as const,
    infinite: (params: ProductListParams) => ["products", "list", "infinite", params] as const,
    detail: (slug: string) => ["products", "detail", slug] as const,
    reviews: (slug: string) => ["products", "detail", slug, "reviews"] as const,
    related: (slug: string) => ["products", "detail", slug, "related"] as const,
  },

  stores: {
    /** Every store query; they carry the viewer's `isFollowing`, so they're reset when the session changes. */
    all: () => ["stores"] as const,
    studio: (limit: number) => ["stores", "studio", limit] as const,
    list: (params: StoreListParams) => ["stores", "list", params] as const,
    detail: (slug: string) => ["stores", "detail", slug] as const,
    products: (slug: string, params: StoreProductParams) => ["stores", "detail", slug, "products", params] as const,
  },

  communities: {
    list: (params: CommunityListParams) => ["communities", "list", params] as const,
    detail: (slug: string) => ["communities", "detail", slug] as const,
    posts: (slug: string) => ["communities", "detail", slug, "posts"] as const,
    members: (slug: string) => ["communities", "detail", slug, "members"] as const,
  },

  jobs: {
    list: (params: JobListParams) => ["jobs", "list", params] as const,
  },

  cart: () => ["cart"] as const,

  orders: {
    list: (params: OrderListParams) => ["orders", "list", params] as const,
  },

  dashboard: {
    summary: (range: DashboardRange) => ["dashboard", "summary", range] as const,
    products: () => ["dashboard", "products"] as const,
    customers: () => ["dashboard", "customers"] as const,
    analytics: (range: DashboardRange) => ["dashboard", "analytics", range] as const,
  },

  notifications: () => ["notifications"] as const,

  usernames: {
    availability: (username: string) => ["usernames", "availability", username] as const,
  },

  /** Real identity data served by the auth server (not MSW). */
  session: () => ["session"] as const,
  handles: {
    availability: (handle: string) => ["handles", "availability", handle] as const,
  },
  account: {
    all: () => ["account"] as const,
    sessions: () => ["account", "sessions"] as const,
    /** Selling data is the signed-in Person's own, so it lives under `account` and is dropped with the session. */
    selling: {
      all: () => ["account", "selling"] as const,
      eligibility: () => ["account", "selling", "eligibility"] as const,
      stores: () => ["account", "selling", "stores"] as const,
      slug: (slug: string) => ["account", "selling", "slug", slug] as const,
      /** Seller products (M4): a store's products, drafts included, and one product's full editor view. */
      storeProducts: (slug: string) => ["account", "selling", "products", slug] as const,
      product: (id: string) => ["account", "selling", "product", id] as const,
      file: (id: string) => ["account", "selling", "file", id] as const,
    },
  },
};
